import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Megaphone, ExternalLink, BadgeCheck, TrendingUp, ShieldCheck, Timer, Lock, BarChart3 } from 'lucide-react';

const TIER_META = {
  elite: { label: 'Elite', color: '#FCD535', minLabel: '500' },
  premium: { label: 'Premium · Verified only', color: '#B26BFF', minLabel: '2 000' },
};

export default function SponsorshipsPanel() {
  const [tier, setTier] = useState('elite');
  const [active, setActive] = useState([]);
  const [mine, setMine] = useState([]);
  const [topInfo, setTopInfo] = useState(null);
  const [markets, setMarkets] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [coinId, setCoinId] = useState('');
  const [sponsorName, setSponsorName] = useState('');
  const [sponsorUrl, setSponsorUrl] = useState('');
  const [price, setPrice] = useState(500);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [analytics, setAnalytics] = useState({});
  const [hasVerified, setHasVerified] = useState(false);

  const load = async (currentTier) => {
    const t = currentTier || tier;
    try {
      const [a, m, top, ms] = await Promise.all([
        api.get('/sponsorships/active'),
        api.get('/markets?limit=30'),
        api.get(`/sponsorships/top_bid?tier=${t}`),
        api.get('/sponsorships/mine').catch(() => ({ data: { sponsorships: [] } })),
      ]);
      setActive((a.data.sponsorships || []).filter(s => s.tier === t));
      setMarkets(m.data.markets || []);
      setTopInfo(top.data);
      setMine(ms.data.sponsorships || []);
      setPrice(top.data?.min_next_bid || (t === 'premium' ? 2000 : 500));
      setHasVerified((ms.data.sponsorships || []).some(s => s.verified));
    } catch (_e) {}
  };
  useEffect(() => { load(tier); /* eslint-disable-next-line */ }, [tier]);

  const switchTier = (t) => {
    if (t === tier) return;
    setTier(t);
    setShowForm(false);
    setExpanded(null);
  };

  const submit = async () => {
    if (!coinId) return toast.error('Pick a coin');
    if (price < (topInfo?.min_next_bid || 500) || price > (tier === 'premium' ? 25000 : 10000)) {
      return toast.error(`Bid must be ${topInfo?.min_next_bid || 500}-${tier === 'premium' ? 25000 : 10000}`);
    }
    setSubmitting(true);
    try {
      await api.post('/sponsorships', {
        coin_id: coinId, sponsor_name: sponsorName, sponsor_url: sponsorUrl,
        price_usdt: Number(price), tier,
      });
      toast.success('Bid placed — you are the top sponsor 🎯');
      setShowForm(false); setCoinId(''); setSponsorName(''); setSponsorUrl('');
      load(tier);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setSubmitting(false);
  };

  const requestVerify = async (sponsorshipId) => {
    try {
      await api.post(`/sponsorships/${sponsorshipId}/verify_request`);
      toast.success('Verification requested — admins will review');
      load(tier);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const openAnalytics = async (sponsorshipId) => {
    if (expanded === sponsorshipId) { setExpanded(null); return; }
    setExpanded(sponsorshipId);
    if (analytics[sponsorshipId]) return;
    try {
      const r = await api.get(`/sponsorships/${sponsorshipId}/analytics`);
      setAnalytics(prev => ({ ...prev, [sponsorshipId]: r.data }));
    } catch (e) { /* ignore */ }
  };

  const minNext = topInfo?.min_next_bid || 500;
  const maxBid = tier === 'premium' ? 25000 : 10000;
  const secondsLeft = topInfo?.seconds_remaining ?? 0;
  const hh = Math.floor(secondsLeft / 3600);
  const mm = Math.floor((secondsLeft % 3600) / 60);
  const urgent = (topInfo?.hours_remaining ?? 999) <= 24;
  const premiumLocked = tier === 'premium' && !hasVerified;

  return (
    <div className="mt-10" data-testid="sponsorships-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-[#FCD535]" />
          <h2 className="text-white text-xl font-semibold">Sponsored Battle Slots</h2>
          <span className="text-xs uppercase tracking-wider text-[#848E9C]">live auction · outbid to win</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex border border-[#2B3139] p-0.5 rounded-sm bg-[#181A20]" data-testid="sp-tier-tabs">
            {['elite', 'premium'].map(t => (
              <button key={t} onClick={() => switchTier(t)} data-testid={`sp-tier-${t}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded-sm ${tier === t ? (t === 'premium' ? 'bg-[#B26BFF] text-white' : 'bg-[#FCD535] text-[#0B0E11]') : 'text-[#B7BDC6] hover:text-white'}`}>
                {TIER_META[t].label}
              </button>
            ))}
          </div>
          <Button size="sm" onClick={() => setShowForm(v => !v)} disabled={premiumLocked}
            className={`rounded-sm font-semibold ${tier === 'premium' ? 'bg-[#B26BFF] hover:bg-[#9A56E3] text-white' : 'bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11]'}`}
            data-testid="toggle-sponsor-form">
            {showForm ? 'Close' : `Bid ${minNext}+ USDT`}
          </Button>
        </div>
      </div>

      {premiumLocked && (
        <div className="mb-3 border border-[#B26BFF]/40 bg-[#B26BFF]/8 p-3 flex items-center gap-2 text-sm" data-testid="premium-locked">
          <Lock className="w-4 h-4 text-[#B26BFF]" />
          <span className="text-[#EAECEF]">Premium slots are <span className="text-[#B26BFF] font-semibold">verified-only</span>. Get any of your Elite sponsorships verified to unlock this tier.</span>
        </div>
      )}

      {topInfo && !premiumLocked && (
        <div className="mb-4 border border-[#F6465D]/30 bg-[#F6465D]/8 p-3 flex flex-wrap items-center gap-3" data-testid="auction-status">
          <TrendingUp className="w-5 h-5 text-[#F6465D]" />
          <div className="flex-1 min-w-0 text-sm">
            {topInfo.top_bid ? (
              <>
                <span className="text-white font-semibold">Current top bid:</span>{' '}
                <span className="font-mono text-[#F6465D]" data-testid="current-top-bid">{topInfo.top_bid.price_usdt} USDT</span>{' '}
                <span className="text-[#B7BDC6]">by {topInfo.top_bid.sponsor_name}</span>
                {topInfo.top_bid.verified && (
                  <span data-testid="top-bid-verified" className="inline-flex items-center gap-0.5 ml-1 text-[#0ECB81]">
                    <BadgeCheck className="w-3.5 h-3.5" />
                  </span>
                )}
                <span className="text-[#B7BDC6] ml-1">· for {topInfo.top_bid.coin_symbol}</span>
              </>
            ) : (
              <span className="text-white">No bids yet — opening bid <span className="font-mono text-[#FCD535]">{minNext} USDT</span></span>
            )}
          </div>
          <div className={`flex items-center gap-1.5 px-2.5 py-1 border rounded-sm ${urgent ? 'border-[#F6465D] bg-[#F6465D]/15 text-[#F6465D] animate-pulse' : 'border-[#2B3139] bg-[#0B0E11] text-[#EAECEF]'}`} data-testid="auction-countdown">
            <Timer className="w-3.5 h-3.5" />
            <span className="font-mono text-sm" data-testid="auction-countdown-value">
              {hh}h {mm.toString().padStart(2, '0')}m
            </span>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase text-[#848E9C]">Min next bid</div>
            <div className="font-mono text-[#FCD535] text-lg" data-testid="min-next-bid">{minNext}</div>
          </div>
        </div>
      )}

      {showForm && !premiumLocked && (
        <div className="border border-[#FCD535]/40 bg-[#FCD535]/8 p-4 mb-4" data-testid="sponsor-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase text-[#848E9C]">Coin</label>
              <select value={coinId} onChange={(e) => setCoinId(e.target.value)}
                className="mt-1 w-full bg-[#0B0E11] border border-[#2B3139] rounded-sm px-3 py-2 text-white text-sm"
                data-testid="sponsor-coin-select">
                <option value="">— pick a coin —</option>
                {markets.map(m => (
                  <option key={m.id} value={m.id}>{m.name} · {m.symbol?.toUpperCase()}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase text-[#848E9C]">Sponsor name</label>
              <Input value={sponsorName} onChange={(e) => setSponsorName(e.target.value)}
                placeholder="e.g. YourProtocol Foundation"
                className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white"
                data-testid="sponsor-name-input" maxLength={60} />
            </div>
            <div>
              <label className="text-xs uppercase text-[#848E9C]">Sponsor URL (optional · verification target)</label>
              <Input value={sponsorUrl} onChange={(e) => setSponsorUrl(e.target.value)}
                placeholder="https://yourproject.com"
                className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white"
                data-testid="sponsor-url-input" maxLength={200} />
            </div>
            <div>
              <label className="text-xs uppercase text-[#848E9C]">Your bid (min {minNext} · max {maxBid} USDT)</label>
              <Input type="number" min={minNext} max={maxBid} step={100} value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono"
                data-testid="sponsor-price-input" />
            </div>
          </div>
          <Button onClick={submit} disabled={submitting}
            className={`mt-4 w-full font-semibold rounded-sm ${tier === 'premium' ? 'bg-[#B26BFF] hover:bg-[#9A56E3] text-white' : 'bg-[#F6465D] hover:bg-[#D63C4F] text-white'}`}
            data-testid="submit-sponsorship-btn">
            {submitting ? 'Placing bid…' : `Place ${TIER_META[tier].label} bid · ${price} USDT`}
          </Button>
        </div>
      )}

      {mine.length > 0 && (
        <div className="mb-3">
          <div className="text-xs uppercase tracking-wider text-[#848E9C] mb-1.5">Your sponsorships</div>
          <div className="grid grid-cols-1 gap-2">
            {mine.slice(0, 8).map(s => {
              const isOpen = expanded === s.sponsorship_id;
              const a = analytics[s.sponsorship_id];
              return (
                <div key={s.sponsorship_id} className="border border-[#2B3139] bg-[#0B0E11]" data-testid={`my-sp-${s.sponsorship_id}`}>
                  <div className="flex items-center gap-2 p-2.5">
                    {s.coin_image && <img src={s.coin_image} alt="" className="w-6 h-6" />}
                    <div className="flex-1 min-w-0 text-xs">
                      <div className="text-white truncate flex items-center gap-1">
                        {s.coin_symbol} · {s.price_usdt?.toFixed(0)}u · {s.tier}
                        {s.verified && <BadgeCheck className="w-3.5 h-3.5 text-[#0ECB81]" data-testid={`mine-verified-${s.sponsorship_id}`} />}
                      </div>
                      <div className="text-[#5E6673] font-mono">{s.status}</div>
                    </div>
                    <button onClick={() => openAnalytics(s.sponsorship_id)}
                      className="text-[10px] uppercase font-mono text-[#B7BDC6] hover:text-white flex items-center gap-1 px-2 py-1 border border-[#2B3139]"
                      data-testid={`toggle-analytics-${s.sponsorship_id}`}>
                      <BarChart3 className="w-3 h-3" /> {isOpen ? 'hide' : 'stats'}
                    </button>
                    {!s.verified && !s.verification_requested && s.status !== 'outbid' && (
                      <button onClick={() => requestVerify(s.sponsorship_id)}
                        className="text-[10px] uppercase font-mono text-[#FCD535] hover:text-white flex items-center gap-1"
                        data-testid={`request-verify-${s.sponsorship_id}`}>
                        <ShieldCheck className="w-3 h-3" /> verify
                      </button>
                    )}
                    {s.verification_requested && !s.verified && (
                      <span className="text-[10px] uppercase font-mono text-[#B7BDC6]">pending review</span>
                    )}
                  </div>
                  {isOpen && (
                    <div className="border-t border-[#2B3139] p-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid={`analytics-${s.sponsorship_id}`}>
                      {a ? <>
                        <div className="border border-[#2B3139] p-2">
                          <div className="text-[10px] uppercase text-[#5E6673]">Total votes</div>
                          <div className="font-mono text-white text-sm">{a.total_votes}</div>
                        </div>
                        <div className="border border-[#2B3139] p-2">
                          <div className="text-[10px] uppercase text-[#5E6673]">Votes for</div>
                          <div className="font-mono text-[#0ECB81] text-sm">{a.votes_for_sponsored}</div>
                        </div>
                        <div className="border border-[#2B3139] p-2">
                          <div className="text-[10px] uppercase text-[#5E6673]">Clicks</div>
                          <div className="font-mono text-[#FCD535] text-sm">{a.clicks} · {a.unique_click_users} unique</div>
                        </div>
                        <div className="border border-[#2B3139] p-2">
                          <div className="text-[10px] uppercase text-[#5E6673]">Engagement</div>
                          <div className="font-mono text-white text-sm">{a.engagement_score}</div>
                          <div className="text-[9px] text-[#5E6673] mt-0.5">
                            {a.cost_per_vote ? `${a.cost_per_vote}/vote` : '—'} · {a.cost_per_click ? `${a.cost_per_click}/click` : '—'}
                          </div>
                        </div>
                        <div className="col-span-full mt-1">
                          <button
                            onClick={async () => {
                              try {
                                const r = await api.get(`/sponsorships/${s.sponsorship_id}/analytics.csv`, { responseType: 'blob' });
                                const blob = new Blob([r.data], { type: 'text/csv' });
                                const url = URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = `sponsorship_${s.sponsorship_id}.csv`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                URL.revokeObjectURL(url);
                                toast.success('CSV downloaded');
                              } catch (_e) {
                                toast.error('CSV download failed');
                              }
                            }}
                            className="inline-flex items-center gap-1.5 text-[10px] uppercase font-mono text-[#0ECB81] hover:text-white border border-[#0ECB81]/40 bg-[#0ECB81]/8 px-2 py-1 rounded-sm"
                            data-testid={`download-csv-${s.sponsorship_id}`}
                          >
                            ⤓ Download CSV
                          </button>
                        </div>
                      </> : <div className="col-span-full text-center text-[#5E6673] py-2">Loading…</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {active.length === 0 ? (
        <div className="border border-[#2B3139] bg-[#181A20] p-5 text-center text-sm text-[#848E9C]">
          No sponsored {tier} slots yet this week. Place the opening bid.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {active.map(s => (
            <div key={s.sponsorship_id}
              className={`border p-4 ${s.status === 'consumed' ? 'border-[#0ECB81]/40 bg-[#0ECB81]/8' : 'border-[#FCD535]/40 bg-[#FCD535]/8'}`}
              data-testid={`sponsorship-${s.sponsorship_id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest font-mono text-[#FCD535]">
                  {s.status === 'consumed' ? 'LIVE THIS WEEK' : 'TOP BID · NEXT WEEK'}
                </div>
                <div className="text-[10px] font-mono text-[#B7BDC6]">{s.tier.toUpperCase()}</div>
              </div>
              <div className="flex items-center gap-3">
                {s.coin_image && <img src={s.coin_image} alt="" className="w-10 h-10" />}
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold text-sm truncate flex items-center gap-1">
                    {s.coin_name} · <span className="text-[#B7BDC6] font-mono">{s.coin_symbol}</span>
                  </div>
                  <div className="text-xs text-[#B7BDC6] mt-0.5 flex items-center gap-1">
                    Sponsored by <span className="text-white font-semibold ml-1">{s.sponsor_name}</span>
                    {s.verified && (
                      <span className="inline-flex items-center gap-0.5 text-[#0ECB81] ml-1" data-testid={`verified-badge-${s.sponsorship_id}`} title="Verified sponsor">
                        <BadgeCheck className="w-3.5 h-3.5" />
                      </span>
                    )}
                    {s.sponsor_url && (
                      <a
                        href={s.sponsor_url}
                        target="_blank" rel="noopener noreferrer"
                        onClick={() => { api.post(`/sponsorships/${s.sponsorship_id}/click`).catch(() => {}); }}
                        className="text-[#FCD535] hover:text-white ml-0.5"
                        data-testid={`sponsor-click-${s.sponsorship_id}`}>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase text-[#848E9C]">Bid</div>
                  <div className="font-mono text-[#0ECB81] text-sm">{s.price_usdt?.toFixed(0)} USDT</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
